// services/ProvidersService.js

import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  collection, 
  query, 
  where, 
  getDocs,
  orderBy,
  limit,
  serverTimestamp,
  deleteDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase/config';

const COLLECTION = 'parrot-providers';

// Helper to remove undefined values
const cleanData = (data) => {
  const cleaned = {};
  Object.keys(data).forEach(key => {
    if (data[key] !== undefined) {
      cleaned[key] = data[key];
    }
  });
  return cleaned;
};

export const ProvidersService = {
  // Create or update provider (upsert)
  async upsert(providerId, data) {
    const docRef = doc(db, COLLECTION, providerId);
    const docSnap = await getDoc(docRef);
    const cleanedData = cleanData(data);
    
    if (docSnap.exists()) {
      await updateDoc(docRef, {
        ...cleanedData,
        updatedAt: serverTimestamp(),
      });
    } else {
      await setDoc(docRef, {
        ...cleanedData,
        id: providerId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        rating: cleanedData.rating ?? 0,
        reviewCount: cleanedData.reviewCount ?? 0,
        jobsCompleted: cleanedData.jobsCompleted ?? 0,
        isVerified: cleanedData.isVerified ?? false,
      });
    }
    
    return (await getDoc(docRef)).data();
  },

  // Update existing provider (uses upsert internally now)
  async update(providerId, data) {
    return this.upsert(providerId, data);
  },

  // Get provider by ID
  async getById(providerId) {
    const docRef = doc(db, COLLECTION, providerId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  },

  // Check if provider exists
  async exists(providerId) {
    const docRef = doc(db, COLLECTION, providerId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists();
  },

  // Get verified providers
  async getVerified(limitCount = 10) {
    const q = query(
      collection(db, COLLECTION),
      where('isVerified', '==', true),
      orderBy('rating', 'desc'),
      limit(limitCount)
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  // Get top rated providers
  async getTopRated(limitCount = 10) {
    const q = query(
      collection(db, COLLECTION),
      orderBy('rating', 'desc'),
      limit(limitCount)
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  // Upload avatar image
  async uploadAvatar(providerId, file) {
    const fileName = `avatar_${Date.now()}.${file.name.split('.').pop()}`;
    const storageRef = ref(storage, `parrot-providers/${providerId}/avatar/${fileName}`);
    
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    
    await this.upsert(providerId, { avatarUrl: downloadURL });
    
    return downloadURL;
  },

  // Upload cover image
  async uploadCover(providerId, file) {
    const fileName = `cover_${Date.now()}.${file.name.split('.').pop()}`;
    const storageRef = ref(storage, `parrot-providers/${providerId}/cover/${fileName}`);
    
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    
    await this.upsert(providerId, { coverUrl: downloadURL });
    
    return downloadURL;
  },

  // Upload portfolio item
  async uploadPortfolioItem(providerId, file, itemData) {
    const fileName = `portfolio_${Date.now()}.${file.name.split('.').pop()}`;
    const storageRef = ref(storage, `parrot-providers/${providerId}/portfolio/${fileName}`);
    
    await uploadBytes(storageRef, file);
    const imageUrl = await getDownloadURL(storageRef);
    
    const portfolioItem = {
      id: `portfolio_${Date.now()}`,
      imageUrl,
      title: itemData.title,
      linkUrl: itemData.linkUrl || '',
      category: itemData.category || '',
      createdAt: new Date().toISOString(),
    };
    
    return portfolioItem;
  },

  // Delete portfolio item image
  async deletePortfolioImage(imageUrl) {
    try {
      const storageRef = ref(storage, imageUrl);
      await deleteObject(storageRef);
    } catch (error) {
      console.warn('Could not delete portfolio image:', error);
    }
  },

  // Increment jobs completed
  async incrementJobCount(providerId) {
    const provider = await this.getById(providerId);
    if (provider) {
      await this.update(providerId, {
        jobsCompleted: (provider.jobsCompleted || 0) + 1,
      });
    }
  },

  // Update rating after review
  async updateRating(providerId, newRating, totalReviews) {
    await this.upsert(providerId, {
      rating: newRating,
      reviewCount: totalReviews,
    });
  },

  // Delete provider
  async delete(providerId) {
    const docRef = doc(db, COLLECTION, providerId);
    await deleteDoc(docRef);
  },
};

export default ProvidersService;